import React, {useState} from 'react'
import styled from '@emotion/styled'
import {Accordion} from '@vio/atlas/Accordion'
import {Button} from '@vio/atlas/Button'
import {Flex} from '@vio/atlas/helpers/Flex'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {omit} from 'ramda'

import {EXCEPTIONAL_SHADES, STANDARD_SHADES} from '../constants'
import {
  applyCssVarsToDOM,
  createBasePaletteVariables,
  createColorGroupVariables,
  createSemanticPaletteVariable,
  generateCssThemeString,
  generatePaletteFromC500,
  getCurrentBasePalette,
  getCurrentSemanticPalette,
  transformPaletteObjectToArray
} from '../utils'
import {saveAs} from '../utils/saveAs'
import {Palette} from './Palette/Palette'
import {UploadSection} from './UploadSection'

interface Props {
  onClose: () => void
}

type ColorShade = {
  value: string | null
  type: 'color'
}

type ColorGroup = Record<string, ColorShade>

type Palette = Record<string, ColorGroup>

// Types for export file content
export type PaletteExport = {
  palette: Palette
}

export type SemanticPaletteExport = {
  semanticPalette: Palette
}

const Wrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s800} ${cssTheme.layout.spacing.s600};
  max-width: 800px;
`

const savePaletteAsFile = (
  content: PaletteExport | SemanticPaletteExport,
  filename: string
) => {
  saveAs(JSON.stringify(content, null, 2), filename, 'json')
}

export const TmsPanel = ({onClose}: Props) => {
  // Filter out neutrals from base palettes
  const [basePalettes, setBasePalettes] = useState(
    transformPaletteObjectToArray(omit(['neutrals'], getCurrentBasePalette()))
  )
  const [semanticPalettes, setSemanticPalettes] = useState(
    getCurrentSemanticPalette()
  )
  // For undo functionality
  const [prevBasePalettes, setPrevBasePalettes] = useState<
    typeof basePalettes | null
  >(null)
  const [autofilledGroup, setAutofilledGroup] = useState<string | null>(null)

  // Get the list of base palette names
  const basePaletteNames = basePalettes.map(palette => palette.group)

  const handleBaseColorChange = (
    paletteGroup: string,
    shade: string,
    newColor: string
  ) => {
    // Update the CSS variable on :root
    const cssVars = createColorGroupVariables({
      group: paletteGroup,
      shades: {[shade]: newColor}
    })

    applyCssVarsToDOM(cssVars)

    // Update local state
    setBasePalettes(currentPalettes => {
      return currentPalettes.map(palette => {
        if (palette.group === paletteGroup) {
          return {
            ...palette,
            shades: {
              ...palette.shades,
              [shade]: newColor
            }
          }
        }
        return palette
      })
    })
  }

  const handleSourcePaletteChange = (
    semanticPalette: string, // neutral, brand, success, etc
    sourcePalette: string // wewardOrange, wewardBlue, etc
  ) => {
    // Update the semantic CSS variables to reference the new base palette variables
    const semanticCssVars = createSemanticPaletteVariable({
      group: semanticPalette,
      referencePalette: sourcePalette
    })

    applyCssVarsToDOM(semanticCssVars)

    setSemanticPalettes(currentPalettes => {
      return currentPalettes.map(palette => {
        if (palette.group === semanticPalette) {
          return {
            ...palette,
            referencePalette: sourcePalette
          }
        }
        return palette
      })
    })
  }

  // Handler to reset all base and semantic palette colors to their original values
  const handleResetBasePalette = () => {
    document.documentElement.removeAttribute('style')
    setBasePalettes(transformPaletteObjectToArray(getCurrentBasePalette()))
    setSemanticPalettes(getCurrentSemanticPalette())
  }

  const handleExportJSON = () => {
    // Export base palette
    const exportedBase: Palette = {}
    basePalettes.forEach(({group, shades}) => {
      exportedBase[group] = {}
      Object.entries(shades).forEach(([shade, color]) => {
        exportedBase[group][shade] = {
          value: color,
          type: 'color'
        }
      })
    })

    // Export semantic palette
    const exportedSemantic: Palette = {}
    semanticPalettes.forEach(({group, referencePalette}) => {
      exportedSemantic[group] = {}
      STANDARD_SHADES.forEach(shade => {
        exportedSemantic[group][shade] = {
          value: `{palette.${referencePalette}.${shade}}`,
          type: 'color'
        }
      })
      if (group === 'neutral') {
        EXCEPTIONAL_SHADES.forEach(shade => {
          const shadeValue = {
            c000: 'white',
            c999: 'black'
          }[shade]
          exportedSemantic[group][shade] = {
            value: `{palette.neutrals.${shadeValue}}`,
            type: 'color'
          }
        })
      }
    })

    // Save both files with a small delay to prevent browser blocking
    savePaletteAsFile({palette: exportedBase}, 'palette.json')
    setTimeout(() => {
      savePaletteAsFile(
        {semanticPalette: exportedSemantic},
        'semanticPalette.json'
      )
    }, 100) // 100ms delay
  }

  const handleExportCSS = () => {
    const cssString = generateCssThemeString(basePalettes, semanticPalettes)

    saveAs(cssString, 'theme.css', 'css')
  }

  // Helper to resolve semantic colors for UI
  const getSemanticColors = (referencePalette: string) => {
    const base = basePalettes.find(p => p.group === referencePalette)
    return base ? base.shades : {}
  }

  // Autofill handler for a palette group
  const handleAutofillShades = (paletteGroup: string) => {
    const palette = basePalettes.find(p => p.group === paletteGroup)
    if (!palette) return

    const {c500} = palette.shades

    if (!c500) return

    // Save previous state for undo
    setPrevBasePalettes(basePalettes)
    setAutofilledGroup(paletteGroup)

    // Generate new shades
    const newShades = generatePaletteFromC500(c500)

    // Update all shades for this group
    setBasePalettes(currentPalettes =>
      currentPalettes.map(p =>
        p.group === paletteGroup
          ? {...p, shades: {...p.shades, ...newShades}}
          : p
      )
    )

    // Also update CSS variables for live preview
    const cssVars = createColorGroupVariables({
      group: paletteGroup,
      shades: newShades
    })

    applyCssVarsToDOM(cssVars)
  }

  // Undo autofill
  const handleUndoAutofill = () => {
    if (prevBasePalettes) {
      setBasePalettes(prevBasePalettes)

      // Restore CSS variables for all groups
      const cssVars = createBasePaletteVariables(prevBasePalettes)
      applyCssVarsToDOM(cssVars)

      setPrevBasePalettes(null)
      setAutofilledGroup(null)
    }
  }

  return (
    <Wrapper>
      <Flex.Row justifyContent="space-between">
        <Text variant="titleXL">Color Palette</Text>
        <Flex.Row gap="s200">
          <Button
            variant="secondary"
            iconStart={<Icon name="ChevronLeft" />}
            size="sm"
            onClick={onClose}
          ></Button>
        </Flex.Row>
      </Flex.Row>

      <Accordion header={'Base Palette'} withDivider>
        <Flex.Column gap="s400">
          {basePalettes.map((palette, index) => {
            return (
              <Palette
                key={index}
                colors={palette.shades}
                paletteName={palette.group}
                onChange={(shade, color) =>
                  handleBaseColorChange(palette.group, shade, color)
                }
                onAutofillShades={() => handleAutofillShades(palette.group)}
                onUndoAutofill={handleUndoAutofill}
                canUndoAutofill={
                  autofilledGroup === palette.group && !!prevBasePalettes
                }
              />
            )
          })}
        </Flex.Column>
      </Accordion>

      <Accordion header={'Semantic Palette'}>
        <Flex.Column gap="s400">
          {semanticPalettes.map((palette, index) => (
            <Palette
              key={index}
              colors={getSemanticColors(palette.referencePalette)}
              paletteName={palette.group}
              isSemanticPalette
              baseColorPalettes={basePaletteNames}
              onSourcePaletteChange={sourcePalette =>
                handleSourcePaletteChange(palette.group, sourcePalette)
              }
              selectedSourcePalette={palette.referencePalette}
            />
          ))}
        </Flex.Column>
      </Accordion>

      {/* Action buttons at the bottom */}
      <Flex.Row gap="s200" style={{marginTop: 32, justifyContent: 'flex-end'}}>
        <Button variant="secondary" size="sm" onClick={handleResetBasePalette}>
          Reset to original
        </Button>
        <Button variant="primary" size="sm" onClick={handleExportJSON}>
          Export JSON
        </Button>
        <Button variant="primary" size="sm" onClick={handleExportCSS}>
          Export CSS
        </Button>
      </Flex.Row>

      {/* S3 Upload Section */}
      <UploadSection
        basePalettes={basePalettes}
        semanticPalettes={semanticPalettes}
      />
    </Wrapper>
  )
}

export default TmsPanel
